import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { CalibrationProfile, CreateCalibrationProfile } from '../types'

export function useCalibrationProfiles() {
  return useQuery({
    queryKey: ['calibration_profiles'],
    queryFn: async (): Promise<CalibrationProfile[]> => {
      const { data, error } = await supabase
        .from('calibration_profiles')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw handleSupabaseError(error)
      return data
    }
  })
}

export function useCalibrationProfile(id: string) {
  return useQuery({
    queryKey: ['calibration_profiles', id],
    queryFn: async (): Promise<CalibrationProfile> => {
      const { data, error } = await supabase
        .from('calibration_profiles')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!id
  })
}

export function useCreateCalibrationProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newProfile: CreateCalibrationProfile): Promise<CalibrationProfile> => {
      const { data, error } = await supabase
        .from('calibration_profiles')
        .insert(newProfile)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration_profiles'] })
    }
  })
}

export function useUpdateCalibrationProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<Omit<CalibrationProfile, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
    }): Promise<CalibrationProfile> => {
      const { data, error } = await supabase
        .from('calibration_profiles')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['calibration_profiles'] })
      queryClient.invalidateQueries({ queryKey: ['calibration_profiles', data.id] })
    }
  })
}

export function useDeleteCalibrationProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('calibration_profiles')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration_profiles'] })
    }
  })
}

// Get default profile with standard coefficients
export function useDefaultCalibrationProfile(): CalibrationProfile {
  return {
    id: 'default',
    user_id: '',
    name: 'Default Profile',
    beta0_base_pace_min_per_km: 6.42,
    beta1_uphill_min_per_m_per_km: 0.035,
    beta2_downhill_min_per_m_per_km: -0.012,
    beta3_fatigue_min_per_km: 0.000,
    use_fatigue: false,
    target_carbs_g_per_h_low: 60.0,
    target_carbs_g_per_h_high: 90.0,
    target_fluids_ml_per_h_low: 500.0,
    target_fluids_ml_per_h_high: 750.0,
    target_sodium_mg_per_h_low: 300.0,
    target_sodium_mg_per_h_high: 800.0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
}